import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Database, ExternalLink, Settings2, ShieldCheck, HelpCircle, Globe, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MysqlGuide = () => {
  const navigate = useNavigate();

  return (
    <>
      <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="flex items-center gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm shrink-0">
              <Database className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Petunjuk Koneksi MySQL/MariaDB</h1>
              <p className="text-sm md:text-lg text-muted-foreground mt-1">
                Hubungkan chatbot Anda langsung ke database MySQL atau MariaDB Anda untuk memberikan jawaban berdasarkan data internal secara real-time.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-amber-900 text-sm flex items-center gap-2">
              Keamanan Adalah Prioritas Utama
            </h4>
            <p className="text-sm leading-relaxed text-amber-800">
              Sangat disarankan untuk membuat user database baru dengan hak akses <strong>Read-Only</strong>. Jangan pernah memberikan user 'root' atau user dengan akses tulis (WRITE/DELETE) demi keamanan data Anda.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Settings2 className="h-6 w-6 text-primary" />
              Langkah 1: Buat User Read-Only
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <p className="text-muted-foreground">Jalankan perintah SQL berikut di server database Anda:</p>
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-md font-mono text-xs overflow-x-auto border-l-4 border-primary max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`CREATE USER 'chatbaze_user'@'%' 
IDENTIFIED BY 'password_anda';

GRANT SELECT ON nama_db.* 
TO 'chatbaze_user'@'%';

FLUSH PRIVILEGES;`}
                </pre>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Globe className="h-6 w-6 text-primary" />
              Langkah 2: Whitelist IP
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <p className="text-muted-foreground">Pastikan firewall server Anda mengizinkan koneksi masuk ke port <strong>3306</strong>. Jika menggunakan hosting seperti cPanel, cari menu <strong>Remote MySQL</strong>.</p>
                <div className="bg-muted p-4 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4 border">
                  <div className="space-y-1 text-center sm:text-left">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Host/IP Server Chatbaze:</p>
                    <code className="text-primary font-mono font-bold text-sm">api.chatbaze.id</code>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => window.open('https://ifconfig.me', '_blank')} className="w-full sm:w-auto">
                    Cek IP Saya
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Info className="h-6 w-6 text-primary" />
            Langkah 3: Konfigurasi di Chatbaze
          </h2>
          <Card className="border-primary/10 shadow-sm">
            <CardContent className="pt-6 space-y-4 text-sm md:text-base">
              <p className="text-muted-foreground">Masukkan detail koneksi di form Data Connector:</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="font-bold text-foreground text-sm mb-1">Host</p>
                  <p className="text-xs text-muted-foreground">IP Publik server database Anda (bukan localhost).</p>
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="font-bold text-foreground text-sm mb-1">Database</p>
                  <p className="text-xs text-muted-foreground">Nama database yang ingin dihubungkan.</p>
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="font-bold text-foreground text-sm mb-1">User & Pass</p>
                  <p className="text-xs text-muted-foreground">User read-only yang dibuat di Langkah 1.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="pt-8 flex flex-col items-center gap-4">
          <p className="text-xs text-muted-foreground">
            Butuh bantuan teknis? Hubungi tim support kami melalui chat di pojok kanan bawah.
          </p>
        </div>
      </div>
    </>
  );
};

export default MysqlGuide;
